import React, { Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import GlobalNatureRiskGate from 'src/app/portfolio/views/global-nature-risk/GlobalNatureRiskGate';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import Loader from 'src/shared/components/loader/Loader';
import { useGetSiteNatureRiskOverviewQuery } from 'src/app/site-nature-risk/state/api/siteNatureRiskEngineSlice';
import { SiteStatus } from 'src/app/site-nature-risk/state/api/siteStatus';
import SiteContextLine from 'src/app/site-nature-risk/components/site-context-line/SiteContextLine';
import SiteInactiveNotice from 'src/app/site-nature-risk/components/site-inactive-notice/SiteInactiveNotice';
import Show from 'src/shared/components/show/Show';
import TimePeriodFilter from 'src/shared/components/filters/time-period-filter/TimePeriodFilter';
import useSiteAvailableTimePeriods from 'src/app/site-nature-risk/hooks/useSiteAvailableTimePeriods';
import useSelectedTimePeriod from 'src/shared/hooks/useSelectedTimePeriod';
import ThreatenedSpeciesWidget from '../../components/threatened-species/ThreatenedSpeciesWidget';
import IucnRedListSummaryWidget from '../../components/threatened-species/IucnRedListSummaryWidget';
import ProtectedAreasSummaryWidget from '../../components/protected-areas/ProtectedAreasSummaryWidget';
import ProtectedAreasWidget from '../../components/protected-areas/ProtectedAreasWidget';

// MapLibre is ~275 kB gzipped, and this is one of its two entry points. Both must
// be lazy, or a static path puts it in the entry chunk for every page. See ADR-003.
// CAUTION: keep this lazy. A static import puts MapLibre (275 KB gzip) in the entry
// chunk, where every page loads it. After a change here, build and make sure that
// `build/index.html` names no `maplibre-*.js`.
const SiteLocationMap = lazy(() => import('../../components/site-location/SiteLocationMap'));

const SiteNatureRiskCharacteristicsContent = () => {
    const { siteId } = useParams<{ siteId: string }>();
    const { currentCustomerId } = useCustomer();
    const navigation = useAppNavigation();
    // Live for every Risk Insights customer — the Characteristics sub-flag retired at go-live.

    // The threatened-species widget is self-fetching; this query only supplies
    // the site name for the header (cached — the overview view uses it too).
    const { data } = useGetSiteNatureRiskOverviewQuery(
        { organisationBusinessCentralId: currentCustomerId, siteId: siteId! },
        { skip: !currentCustomerId || !siteId }
    );

    // The protected-areas widget honours the selected time period; the species
    // widgets are S3-grid-backed and always show the current snapshot.
    const { timePeriods } = useSiteAvailableTimePeriods(currentCustomerId, siteId);
    const { selectedAt, setSelectedAt } = useSelectedTimePeriod();

    const site = data?.site;
    const isInactive = site !== undefined && site !== null && site.status !== SiteStatus.ACTIVE;
    const breadcrumbs = [{ label: 'Nature Risk', path: navigation.globalNatureRiskOverviewPath }, { label: site?.name ?? '' }];

    return (
        <StandardAppLayout
            title={site?.name ?? ''}
            subTitle='Characteristics'
            breadcrumbs={breadcrumbs}
            stickyHeader={false}
            classNames={{ otherActions: '!justify-start !p-0' }}
            otherActions={<TimePeriodFilter periods={timePeriods} selectedValue={selectedAt} onSelect={setSelectedAt} />}
            mainContent={
                <div className='flex flex-col w-full gap-6'>
                    <SiteContextLine
                        countryName={site?.geography?.countryName ?? null}
                        activity={site?.activity}
                        status={site?.status}
                        archivedAt={site?.validTo ?? null}
                    />
                    <Show when={!isInactive} fallback={<SiteInactiveNotice />}>
                        {/* One grid rather than two independent columns, so each row's
                            cards share a height: the summaries align, and the lists below
                            them align. Source order is the mobile stack (species pair, then
                            protected-areas pair); `md:order-*` interleaves them into rows. */}
                        <div className='grid w-full grid-cols-1 gap-6 md:grid-cols-2' data-testid='site-nature-risk-characteristics-main'>
                            <div className='flex md:order-1'>
                                <IucnRedListSummaryWidget organisationBusinessCentralId={currentCustomerId} siteId={siteId!} />
                            </div>
                            <div className='flex md:order-3'>
                                <ThreatenedSpeciesWidget organisationBusinessCentralId={currentCustomerId} siteId={siteId!} />
                            </div>
                            <div className='flex md:order-2'>
                                <ProtectedAreasSummaryWidget
                                    organisationBusinessCentralId={currentCustomerId}
                                    siteId={siteId!}
                                    at={selectedAt ?? undefined}
                                />
                            </div>
                            {/* `min-w-0`: a grid item defaults to `min-width: auto`, so without
                                it a long area name widens the whole column rather than being
                                clipped inside the card. */}
                            <div className='flex min-w-0 md:order-4'>
                                <ProtectedAreasWidget
                                    organisationBusinessCentralId={currentCustomerId}
                                    siteId={siteId!}
                                    at={selectedAt ?? undefined}
                                />
                            </div>
                        </div>
                    </Show>
                    {/* Full width, closing the page. Its own Show so each one keeps a
                        single child; only an active site has a footprint to draw. */}
                    <Show when={!isInactive}>
                        <Suspense fallback={<Loader />}>
                            <SiteLocationMap
                                organisationBusinessCentralId={currentCustomerId}
                                siteId={siteId!}
                                at={selectedAt ?? undefined}
                                onOpenInSiteExplorer={() => navigation.toGlobalNatureRiskSiteExplorerMap(siteId)}
                            />
                        </Suspense>
                    </Show>
                </div>
            }
        />
    );
};

/** SiteNatureRiskCharacteristics, shown only to a user who may see Global Nature Risk. */
const SiteNatureRiskCharacteristics = () => (
    <GlobalNatureRiskGate>
        <SiteNatureRiskCharacteristicsContent />
    </GlobalNatureRiskGate>
);

export default SiteNatureRiskCharacteristics;
